import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft,
  User,
  Mail,
  Phone,
  Building,
  MapPin,
  Calendar,
  Clock,
  DollarSign,
  Package,
  FileText,
  CheckCircle,
  AlertCircle,
  XCircle,
  Receipt,
  Edit,
  Save,
  X
} from 'lucide-react';
import { purchaseRequestApiService, PurchaseRequest } from '../services/purchaseRequestApi';

const DetailsDemandeAchatPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [request, setRequest] = useState<PurchaseRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingStatus, setEditingStatus] = useState(false);
  const [newStatus, setNewStatus] = useState<string>('');

  useEffect(() => {
    if (id) {
      loadPurchaseRequest(id);
    }
  }, [id]);

  const loadPurchaseRequest = async (requestId: string) => {
    try {
      setLoading(true);
      const data = await purchaseRequestApiService.getPurchaseRequestAdmin(requestId);
      console.log('📋 Données reçues du backend:', data);
      console.log('📅 created_at:', data.created_at, typeof data.created_at);
      console.log('📅 updated_at:', data.updated_at, typeof data.updated_at);
      setRequest(data);
      setNewStatus(data.status);
      setError(null);
    } catch (err) {
      console.error('Erreur lors du chargement de la demande:', err);
      setError('Erreur lors du chargement de la demande d\'achat');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async () => {
    if (!request || !id) return;

    try {
      await purchaseRequestApiService.updatePurchaseRequestStatus(id, newStatus as any);
      setRequest({ ...request, status: newStatus as any });
      setEditingStatus(false);
    } catch (err) {
      console.error('Erreur lors de la mise à jour du statut:', err);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      case 'revision':
        return <AlertCircle className="w-6 h-6 text-amber-500" />;
      case 'pending':
        return <Clock className="w-6 h-6 text-blue-500" />;
      case 'cancelled':
        return <XCircle className="w-6 h-6 text-red-500" />;
      default:
        return <Clock className="w-6 h-6 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'revision':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'pending':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'Confirmée';
      case 'revision':
        return 'En révision';
      case 'pending':
        return 'En attente';
      case 'cancelled':
        return 'Annulée';
      default:
        return status;
    }
  };

  const formatPrice = (price: number | undefined | null) => {
    const safePrice = price || 0;
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0
    }).format(safePrice);
  };

  const safeToFixed = (value: any, decimals: number = 2) => {
    const num = Number(value || 0);
    return isNaN(num) ? '0.00' : num.toFixed(decimals);
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Non disponible';
    
    // Essayer différents formats de date
    let date: Date;
    
    // Si c'est déjà un timestamp ou une date ISO
    if (typeof dateString === 'string' && dateString.includes('T')) {
      date = new Date(dateString);
    } else if (typeof dateString === 'string' && dateString.includes('-')) {
      date = new Date(dateString);
    } else {
      // Essayer de parser comme timestamp
      const timestamp = parseInt(dateString);
      if (!isNaN(timestamp)) {
        date = new Date(timestamp);
      } else {
        date = new Date(dateString);
      }
    }
    
    if (isNaN(date.getTime())) {
      console.error('Date invalide reçue:', dateString);
      return 'Date invalide';
    }
    
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const viewInvoice = () => {
    if (!request) return;

    const selectedServices = request.items.map(item => ({
      service: item.service ? {
        id: item.service.toString(),
        name: item.service_name || 'Service',
        price: item.unit_price,
        price_type: 'fixed' as const
      } : undefined,
      pack: item.pack ? {
        id: item.pack.toString(),
        name: item.pack_name || 'Pack',
        total_price: item.unit_price
      } : undefined,
      type: item.type,
      quantity: item.quantity,
      pages: item.pages,
      description: item.description
    }));

    navigate('/facture-proforma', {
      state: {
        selectedServices,
        promoDiscount: request.discount_percentage,
        promoCode: request.promo_code,
        clientInfo: {
          name: request.client_name,
          email: request.client_email,
          phone: request.client_phone,
          company: request.client_company,
          address: request.client_address
        }
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement de la demande...</p>
        </div>
      </div>
    );
  }

  if (error || !request) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error || 'Demande non trouvée'}</p>
          <button
            onClick={() => navigate('/demandesclients')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* En-tête */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/demandesclients')}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Retour</span>
              </button>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    Facture {request.invoice_id || `#${request.id}`}
                  </h1>
                  <p className="text-gray-600">
                    Créée le {formatDate(request.created_at)}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={viewInvoice}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Receipt className="w-4 h-4" />
                <span>Voir facture</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Colonne principale */}
          <div className="lg:col-span-2 space-y-6">
            {/* Statut et informations générales */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Statut de la demande</h2>
                {!editingStatus ? (
                  <button
                    onClick={() => setEditingStatus(true)}
                    className="flex items-center space-x-2 px-3 py-1 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                    <span>Modifier</span>
                  </button>
                ) : (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={updateStatus}
                      className="flex items-center space-x-1 px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <Save className="w-4 h-4" />
                      <span>Sauver</span>
                    </button>
                    <button
                      onClick={() => {
                        setEditingStatus(false);
                        setNewStatus(request.status);
                      }}
                      className="flex items-center space-x-1 px-3 py-1 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4" />
                      <span>Annuler</span>
                    </button>
                  </div>
                )}
              </div>

              {!editingStatus ? (
                <div className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full border ${getStatusColor(request.status)}`}>
                  {getStatusIcon(request.status)}
                  <span className="font-medium">{getStatusLabel(request.status)}</span>
                </div>
              ) : (
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="pending">En attente</option>
                  <option value="confirmed">Confirmée</option>
                  <option value="revision">En révision</option>
                  <option value="cancelled">Annulée</option>
                </select>
              )}

              {request.has_variable_pricing && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center space-x-2 text-amber-800">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      Cette demande contient des prix variables nécessitant une révision
                    </span>
                  </div>
                </div>
              )}
            </motion.div>

            {/* Items commandés */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Items commandés</h2>
              <div className="space-y-4">
                {request.items.map((item, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Package className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-medium text-gray-900">
                              {item.service_name || item.pack_name || `${item.type} #${index + 1}`}
                            </h3>
                            <div className="flex items-center space-x-4 text-sm text-gray-600">
                              <span className="capitalize bg-gray-100 px-2 py-1 rounded">
                                {item.type}
                              </span>
                              <span>Quantité: {item.quantity}</span>
                              {item.pages && <span>Pages: {item.pages}</span>}
                            </div>
                          </div>
                        </div>
                        {item.description && (
                          <p className="text-sm text-gray-600 mt-2 ml-13">
                            {item.description}
                          </p>
                        )}
                      </div>
                      <div className="text-right ml-4">
                        <div className="text-lg font-semibold text-gray-900">
                          {formatPrice(item.total_price)}
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatPrice(item.unit_price || 0)} unitaire
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Informations client */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Informations client</h2>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <User className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Nom</p>
                    <p className="font-medium text-gray-900">
                      {request.client_name || request.user_name || 'Non renseigné'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium text-gray-900">
                      {request.client_email || 'Non renseigné'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Téléphone</p>
                    <p className="font-medium text-gray-900">
                      {request.client_phone || 'Non renseigné'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Building className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Entreprise</p>
                    <p className="font-medium text-gray-900">
                      {request.client_company || 'Non renseigné'}
                    </p>
                  </div>
                </div>
                {request.client_address && (
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Adresse</p>
                      <p className="font-medium text-gray-900">
                        {request.client_address}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Résumé financier */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Résumé financier</h2>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Sous-total</span>
                  <span className="font-medium">{formatPrice(request.subtotal)}</span>
                </div>
                {request.discount_percentage && Number(request.discount_percentage) > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Réduction ({safeToFixed(request.discount_percentage)}%)</span>
                    <span>-{formatPrice(request.discount_amount)}</span>
                  </div>
                )}
                {request.tva_percentage && Number(request.tva_percentage) > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">TVA ({safeToFixed(request.tva_percentage)}%)</span>
                    <span className="font-medium">{formatPrice(request.tva_amount)}</span>
                  </div>
                )}
                <div className="border-t pt-3">
                  <div className="flex justify-between">
                    <span className="text-lg font-semibold text-gray-900">Total</span>
                    <span className="text-lg font-bold text-gray-900">
                      {formatPrice(request.total_amount)}
                    </span>
                  </div>
                </div>
                {request.promo_code && (
                  <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded">
                    <p className="text-sm text-green-700">
                      <span className="font-medium">Code promo:</span> {request.promo_code}
                    </p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Métadonnées */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Informations système</h2>
              <div className="space-y-3 text-sm">
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">Créée le:</span>
                  <span className="font-medium text-gray-900">{formatDate(request.created_at)}</span>
                </div>
                {request.updated_at && request.updated_at !== request.created_at && (
                  <div className="flex items-center space-x-2">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">Modifiée le:</span>
                    <span className="font-medium text-gray-900">{formatDate(request.updated_at)}</span>
                  </div>
                )}
                <div className="flex items-center space-x-2">
                  <FileText className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">ID Facture:</span>
                  <span className="font-medium text-gray-900">{request.invoice_id || `#${request.id}`}</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailsDemandeAchatPage;
